import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  FAQ_HEADLINE,
  FAQ_ITEMS,
  FAQ_SCAN_LINE,
  FAQ_SCAN_PILLS,
  FAQ_STAMP,
} from "./faqContent";

const verdictToneClass = (tone, highlight) => {
  if (highlight) return "text-orange-400 border-orange-500/35 bg-orange-500/10";
  if (tone === "teal") return "text-teal-400 border-teal-500/30 bg-teal-500/10";
  if (tone === "hero") return "text-orange-400 border-orange-500/30 bg-orange-500/8";
  return "text-gray-300 border-white/10 bg-white/[0.03]";
};

const FaqSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 bg-[#0a0c0f] overflow-hidden"
      data-testid="faq-section"
      ref={ref}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 47px, rgba(255,255,255,0.03) 47px, rgba(255,255,255,0.03) 48px)",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <p className="badge-mono text-[10px] font-bold tracking-[0.28em] text-orange-400/90 uppercase mb-4">
              {FAQ_STAMP}
            </p>

            <h2
              className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-3"
              data-testid="faq-headline"
            >
              {FAQ_HEADLINE.lead}{" "}
              <span className="text-orange-400">{FAQ_HEADLINE.accent}</span>
            </h2>

            <p className="text-sm font-mono text-gray-500 mb-5">{FAQ_SCAN_LINE}</p>

            <div
              className="flex flex-wrap gap-2"
              data-testid="faq-scan-pills"
            >
              {FAQ_SCAN_PILLS.map((pill) => (
                <div
                  key={pill.label}
                  className="flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.02]"
                >
                  <span
                    className={`font-mono font-bold text-sm leading-none ${
                      pill.value.startsWith("$") || pill.value === "24/7"
                        ? "text-orange-400"
                        : pill.value === "1-CLICK"
                          ? "text-teal-400"
                          : "text-gray-400"
                    }`}
                  >
                    {pill.value}
                  </span>
                  <span className="text-[9px] font-mono text-gray-600 tracking-wide">
                    {pill.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div
              className="rounded-2xl border border-white/[0.08] bg-[#0c1018]/60 overflow-hidden divide-y divide-white/[0.06]"
              data-testid="faq-verdict-ledger"
            >
              {FAQ_ITEMS.map((item, index) => {
                const isHero = item.verdictTone === "hero";

                return (
                  <motion.div
                    key={item.id}
                    initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={
                      reduceMotion || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }
                    }
                    transition={{ duration: 0.35, delay: index * 0.07 }}
                    className={`flex flex-col sm:flex-row gap-4 sm:gap-5 px-4 sm:px-6 py-5 ${
                      isHero ? "bg-orange-500/[0.03]" : ""
                    }`}
                    data-testid={`faq-item-${index}`}
                  >
                    <div className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2 sm:w-28 flex-shrink-0">
                      <span className="badge-mono text-[10px] text-gray-600">{item.index}</span>
                      <span
                        className={`inline-flex items-center justify-center font-mono font-bold text-lg sm:text-xl leading-none px-3 py-2 rounded-lg border min-w-[4.5rem] ${verdictToneClass(
                          item.verdictTone,
                          isHero
                        )}`}
                      >
                        {item.verdict}
                      </span>
                      {isHero && (
                        <span className="badge-mono text-[8px] text-orange-400/80 tracking-wider hidden sm:block">
                          TOP CONCERN
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-sm sm:text-base text-[#F3F4F6] mb-2">
                        {item.question}
                      </p>
                      <p className="text-[11px] sm:text-sm text-[#9CA3AF] font-body leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
